import { defineMessages } from "../i18n/core";

export const addMachinesMessages = defineMessages({
  "pt-BR": {
    defaultLabel: "Estações do financeiro",
    created: "Instaladores gerados. Distribua antes do vencimento.",
    createFailed: "Não foi possível gerar os instaladores.",
    title: "Adicionar máquinas",
    lede: "Gere instaladores já vinculados a esta organização. Quem instalar aparece em Máquinas com o grupo e a política escolhidos, sem digitar nada.",
    configureTitle: "1. Configure o registro",
    batchName: "Nome do lote",
    batchNameHint: "Aparece na auditoria e na lista de registros.",
    group: "Grupo",
    policy: "Política",
    howMany: "Quantas máquinas",
    single: "Uma máquina",
    batch: "Implantação em lote",
    maxUses: "Limite de máquinas",
    maxUsesHint: "O instalador para de registrar ao atingir o limite.",
    validFor: "Válido por",
    validity: { 4: "4 horas", 24: "24 horas", 72: "3 dias", 168: "7 dias", 720: "30 dias" },
    securityTip: "Prefira validade curta e limite justo. Um instalador vazado só registra máquinas até vencer ou atingir o limite, e toda máquina registrada aparece aqui para revogação.",
    generate: "Gerar instaladores",
    installTitle: "2. Instale nas máquinas",
    installSub: (label: string, date: string) => `${label} · vence ${date}`,
    installSubEmpty: "Os instaladores aparecem aqui",
    installEmpty: "Configure o registro ao lado e gere os instaladores para Windows, macOS e Linux.",
    osTabs: "Sistema operacional",
    mockDownload: (file: string) => `Simulação: ${file} seria baixado.`,
    downloadFile: (file: string) => `Baixar ${file}`,
    bundleTip: "O arquivo contém o token e só pode ser baixado agora. Este servidor não tem os pacotes do agente: instale o agente por outro meio e rode o comando abaixo na mesma pasta do arquivo.",
    enrollCommand: "Registro do agente (como administrador)",
    stepAgent: "Baixe o agente",
    archHint: {
      windows: "ARM64 só para Windows em processador ARM, como Surface Pro X e Copilot+ PC.",
      macos: "Apple Silicon para Macs com chip M1 ou mais novo; Intel para os anteriores.",
      linux: "ARM64 para servidores Graviton, Ampere e Raspberry Pi 4 ou mais novo.",
    },
    version: (v: string) => `Versão ${v}`,
    checksums: "Conferir SHA-256",
    stepBundle: "Baixe o arquivo de registro",
    bundleOnce: (n: number) => `Contém o token e só pode ser baixado agora. ${n === 1 ? "Registra 1 máquina." : `Serve para até ${n} máquinas.`}`,
    stepRun: {
      windows: "Extraia o .zip, ponha o arquivo de registro na mesma pasta e execute no PowerShell como administrador",
      macos: "Extraia o .tar.gz, ponha o arquivo de registro na mesma pasta e execute no Terminal",
      linux: "Extraia o .tar.gz, ponha o arquivo de registro na mesma pasta e execute",
    },
    deployHint: {
      windows: "O mesmo comando serve para GPO, Intune e RMM.",
      macos: "O mesmo comando serve para Jamf e outros MDM.",
      linux: "O mesmo comando serve para Ansible, Salt e scripts de implantação.",
    },
    commandLabel: (os: string) => `comando de instalação ${os}`,
    // {fda} é trocado pelo termo em negrito.
    macFullDisk: "O macOS exige conceder {fda} ao agente. Com MDM, distribua o perfil PPPC junto; sem MDM, o usuário concede em Ajustes do Sistema › Privacidade e Segurança.",
    macFullDiskTerm: "Acesso Total ao Disco",
    windowsNote: "O agente fica em Arquivos de Programas\\E-BACKUP e roda como o serviço E-BACKUP Agent, com a conta SYSTEM. Executar de novo numa máquina registrada atualiza o agente e mantém o registro.",
    linuxNote: "Requer systemd. Executar de novo numa máquina registrada atualiza o agente e mantém o registro.",
    deleteBundle: "Apague o arquivo de registro ao terminar a implantação; o agente não precisa dele para funcionar.",
    issuedTitle: "Registros emitidos",
    issuedSub: "Tokens nunca são exibidos de novo depois de gerados",
    colBatch: "Lote",
    colGroup: "Grupo",
    colUse: "Uso",
    colValidity: "Validade",
    colCreatedBy: "Criado por",
    uses: (used: number, max: number) => `${used} de ${max}`,
    expired: (ago: string) => `Vencido ${ago}`,
    exhausted: "Esgotado",
    activeUntil: (date: string) => `Ativo até ${date}`,
  },
  "en-US": {
    defaultLabel: "Finance workstations",
    created: "Installers generated. Distribute them before they expire.",
    createFailed: "Could not generate the installers.",
    title: "Add machines",
    lede: "Generate installers already linked to this organization. Machines that install them show up in Machines with the chosen group and policy, with nothing to type.",
    configureTitle: "1. Configure enrollment",
    batchName: "Batch name",
    batchNameHint: "Shown in the audit trail and in the enrollment list.",
    group: "Group",
    policy: "Policy",
    howMany: "How many machines",
    single: "One machine",
    batch: "Batch deployment",
    maxUses: "Machine limit",
    maxUsesHint: "The installer stops enrolling once the limit is reached.",
    validFor: "Valid for",
    validity: { 4: "4 hours", 24: "24 hours", 72: "3 days", 168: "7 days", 720: "30 days" },
    securityTip: "Prefer a short validity and a tight limit. A leaked installer can only enroll machines until it expires or reaches the limit, and every enrolled machine shows up here for revocation.",
    generate: "Generate installers",
    installTitle: "2. Install on the machines",
    installSub: (label: string, date: string) => `${label} · expires ${date}`,
    installSubEmpty: "Installers appear here",
    installEmpty: "Configure enrollment on the left and generate installers for Windows, macOS and Linux.",
    osTabs: "Operating system",
    mockDownload: (file: string) => `Simulation: ${file} would be downloaded.`,
    downloadFile: (file: string) => `Download ${file}`,
    bundleTip: "The file contains the token and can only be downloaded now. This server has no agent packages: install the agent another way and run the command below in the same folder as the file.",
    enrollCommand: "Agent enrollment (as administrator)",
    stepAgent: "Download the agent",
    archHint: {
      windows: "ARM64 only for Windows on ARM processors, such as Surface Pro X and Copilot+ PCs.",
      macos: "Apple Silicon for Macs with an M1 chip or newer; Intel for older ones.",
      linux: "ARM64 for Graviton, Ampere and Raspberry Pi 4 or newer.",
    },
    version: (v: string) => `Version ${v}`,
    checksums: "Verify SHA-256",
    stepBundle: "Download the enrollment file",
    bundleOnce: (n: number) => `Contains the token and can only be downloaded now. ${n === 1 ? "Enrolls 1 machine." : `Works for up to ${n} machines.`}`,
    stepRun: {
      windows: "Extract the .zip, put the enrollment file in the same folder and run in PowerShell as administrator",
      macos: "Extract the .tar.gz, put the enrollment file in the same folder and run in Terminal",
      linux: "Extract the .tar.gz, put the enrollment file in the same folder and run",
    },
    deployHint: {
      windows: "The same command works with GPO, Intune and RMM tools.",
      macos: "The same command works with Jamf and other MDM tools.",
      linux: "The same command works with Ansible, Salt and deployment scripts.",
    },
    commandLabel: (os: string) => `${os} install command`,
    macFullDisk: "macOS requires granting {fda} to the agent. With MDM, distribute the PPPC profile alongside it; without MDM, the user grants it in System Settings › Privacy & Security.",
    macFullDiskTerm: "Full Disk Access",
    windowsNote: "The agent goes to Program Files\\E-BACKUP and runs as the E-BACKUP Agent service under the SYSTEM account. Running it again on an enrolled machine updates the agent and keeps the enrollment.",
    linuxNote: "Requires systemd. Running it again on an enrolled machine updates the agent and keeps the enrollment.",
    deleteBundle: "Delete the enrollment file when the rollout is done; the agent does not need it to run.",
    issuedTitle: "Issued enrollments",
    issuedSub: "Tokens are never shown again after they are generated",
    colBatch: "Batch",
    colGroup: "Group",
    colUse: "Used",
    colValidity: "Validity",
    colCreatedBy: "Created by",
    uses: (used: number, max: number) => `${used} of ${max}`,
    expired: (ago: string) => `Expired ${ago}`,
    exhausted: "Used up",
    activeUntil: (date: string) => `Active until ${date}`,
  },
  es: {
    defaultLabel: "Estaciones de finanzas",
    created: "Instaladores generados. Distribúyelos antes de que venzan.",
    createFailed: "No se pudieron generar los instaladores.",
    title: "Agregar equipos",
    lede: "Genera instaladores ya vinculados a esta organización. Los equipos que los instalen aparecen en Equipos con el grupo y la política elegidos, sin escribir nada.",
    configureTitle: "1. Configura el registro",
    batchName: "Nombre del lote",
    batchNameHint: "Aparece en la auditoría y en la lista de registros.",
    group: "Grupo",
    policy: "Política",
    howMany: "Cuántos equipos",
    single: "Un equipo",
    batch: "Implementación por lotes",
    maxUses: "Límite de equipos",
    maxUsesHint: "El instalador deja de registrar al alcanzar el límite.",
    validFor: "Válido por",
    validity: { 4: "4 horas", 24: "24 horas", 72: "3 días", 168: "7 días", 720: "30 días" },
    securityTip: "Prefiere una validez corta y un límite ajustado. Un instalador filtrado solo registra equipos hasta vencer o alcanzar el límite, y cada equipo registrado aparece aquí para revocarlo.",
    generate: "Generar instaladores",
    installTitle: "2. Instala en los equipos",
    installSub: (label: string, date: string) => `${label} · vence ${date}`,
    installSubEmpty: "Los instaladores aparecen aquí",
    installEmpty: "Configura el registro al lado y genera los instaladores para Windows, macOS y Linux.",
    osTabs: "Sistema operativo",
    mockDownload: (file: string) => `Simulación: se descargaría ${file}.`,
    downloadFile: (file: string) => `Descargar ${file}`,
    bundleTip: "El archivo contiene el token y solo se puede descargar ahora. Este servidor no tiene los paquetes del agente: instala el agente por otro medio y ejecuta el siguiente comando en la misma carpeta del archivo.",
    enrollCommand: "Registro del agente (como administrador)",
    stepAgent: "Descarga el agente",
    archHint: {
      windows: "ARM64 solo para Windows en procesadores ARM, como Surface Pro X y Copilot+ PC.",
      macos: "Apple Silicon para Mac con chip M1 o posterior; Intel para los anteriores.",
      linux: "ARM64 para servidores Graviton, Ampere y Raspberry Pi 4 o posterior.",
    },
    version: (v: string) => `Versión ${v}`,
    checksums: "Verificar SHA-256",
    stepBundle: "Descarga el archivo de registro",
    bundleOnce: (n: number) => `Contiene el token y solo se puede descargar ahora. ${n === 1 ? "Registra 1 equipo." : `Sirve para hasta ${n} equipos.`}`,
    stepRun: {
      windows: "Extrae el .zip, coloca el archivo de registro en la misma carpeta y ejecuta en PowerShell como administrador",
      macos: "Extrae el .tar.gz, coloca el archivo de registro en la misma carpeta y ejecuta en Terminal",
      linux: "Extrae el .tar.gz, coloca el archivo de registro en la misma carpeta y ejecuta",
    },
    deployHint: {
      windows: "El mismo comando sirve para GPO, Intune y herramientas RMM.",
      macos: "El mismo comando sirve para Jamf y otros MDM.",
      linux: "El mismo comando sirve para Ansible, Salt y scripts de implementación.",
    },
    commandLabel: (os: string) => `comando de instalación ${os}`,
    macFullDisk: "macOS exige otorgar {fda} al agente. Con MDM, distribuye el perfil PPPC junto con él; sin MDM, el usuario lo otorga en Ajustes del Sistema › Privacidad y seguridad.",
    macFullDiskTerm: "Acceso total al disco",
    windowsNote: "El agente queda en Archivos de programa\\E-BACKUP y se ejecuta como el servicio E-BACKUP Agent con la cuenta SYSTEM. Ejecutarlo de nuevo en un equipo registrado actualiza el agente y conserva el registro.",
    linuxNote: "Requiere systemd. Ejecutarlo de nuevo en un equipo registrado actualiza el agente y conserva el registro.",
    deleteBundle: "Borra el archivo de registro al terminar la implementación; el agente no lo necesita para funcionar.",
    issuedTitle: "Registros emitidos",
    issuedSub: "Los tokens nunca se vuelven a mostrar después de generarse",
    colBatch: "Lote",
    colGroup: "Grupo",
    colUse: "Uso",
    colValidity: "Validez",
    colCreatedBy: "Creado por",
    uses: (used: number, max: number) => `${used} de ${max}`,
    expired: (ago: string) => `Vencido ${ago}`,
    exhausted: "Agotado",
    activeUntil: (date: string) => `Activo hasta ${date}`,
  },
});
